import AddIcon from '@mui/icons-material/Add';
import { Grid, Box } from '@mui/material';
import { GridSortModel } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useCallback, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSearchParams } from 'react-router-dom';
import { CrmJobDTO, CrmJobFilterDTO, IUserDTO } from 'wisestep-api';
import {
  WsButton,
  WsColors,
  WsDrawer,
  WsIconNoUserGeneric,
  WsListingLoader,
  WsTypography,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard/NoDataFoundCard';
import { useUserContext } from '../../../context/UserContext';
import { StyledGrid } from '../../../features/clientListingPage/components/ClientList';
import CommonHeader from '../../../features/leads/components/CommonHeader';
import { CrmJobsAccess, useCrmJobsAuthz } from '../../../hooks/useCrmJobsAuthz';
import ErrorLogger from '../../../providers/ErrorLogger';
import getDecodedQueryParams from '../../../utils/getDecodedQueryParams';
import {
  crmJobsTypes,
  defaultCrmJobFilter,
  mapToCrmJobAdvanceFilter,
  getAllAvailableColumns,
  downloadCrmJobsA,
} from '../helper/crmJobHelper';
import { useCrmJobs } from '../hooks/useCrmJobs';

import CrmJobData from './CrmJobData';
import CrmJobDownloadField from './CrmJobDownloadField';
import CrmJobDrawer from './CrmJobDrawer';
import CrmJobFilters from './CrmJobFilters';
import CrmJobsAdvanceFilters from './CrmJobsAdvanceFilters';

const CrmJobList = () => {
  const [param] = useSearchParams();
  const queryData = param.get('d') || '';
  const getDecodedFilters = useCallback((): CrmJobFilterDTO => {
    if (!queryData) return defaultCrmJobFilter;
    try {
      return getDecodedQueryParams<CrmJobFilterDTO>(queryData) ?? defaultCrmJobFilter;
    } catch (error) {
      console.error('Failed to decode query params:', error);
      return defaultCrmJobFilter;
    }
  }, [queryData]);

  const [openAdvancedFilters, setOpenAdvancedFilters] = useState<boolean>(false);
  const [jobType, setJobType] = useState<string>('all_crm_jobs');
  const [searchQuery, setSearchQuery] = useState<CrmJobFilterDTO>(getDecodedFilters);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'created_on', sort: 'desc' },
  ]);
  const [advancedFilterDefaults, setAdvancedFilterDefaults] =
    useState<CrmJobFilterDTO>(searchQuery);
  const [openCreateCrmJobDrawer, setOpenCreateCrmJobDrawer] = useState<boolean>(false);
  const [openDownload, setOpenDownload] = useState<boolean>(false);
  const [downloadType, setDownloadType] = useState<string>('');

  const handleDownload = async (type: string) => {
    setOpenDownload(true);
    setDownloadType(type);
  };

  const handleDownloadCrmJobs = async (selectedColumns: string[]) => {
    try {
      await downloadCrmJobsA(selectedColumns, searchQuery, downloadType);
      setOpenDownload(false);
    } catch (error) {
      console.error('Error downloading CRM jobs:', error);
      setOpenDownload(false);
    }
  };

  const isAuthorizedAddCrmJob = useCrmJobsAuthz(CrmJobsAccess.ADD_CRM_JOB_DETAILS);

  const { jobs, loading, fetchCrmJobs } = useCrmJobs(searchQuery, sortModel);

  const onClear = () => setSearchQuery(defaultCrmJobFilter);
  const getJobType = (jobType) => {
    setJobType(jobType);
    const jobOwners = jobType === 'my_crm_jobs' ? [loggedInUser.email ?? ''] : [];
    setSearchQuery((prevQuery) => ({
      ...prevQuery,
      jobOwners: jobOwners,
    }));
  };

  const onFilterFormSubmit = (data: CrmJobFilterDTO) => {
    const updatedData = {
      ...data,
      status: searchQuery.jobStatuses,
      query: searchQuery.query,
    };
    setSearchQuery((prevQuery) => ({
      ...prevQuery,
      ...mapToCrmJobAdvanceFilter(updatedData),
    }));
    setAdvancedFilterDefaults(data);
  };

  return (
    <>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ padding: 2, background: WsColors.White, borderRadius: 2, mb: 4 }}
      >
        <Grid item>
          <CommonHeader
            title={jobType === 'my_crm_jobs' ? 'My Jobs' : 'All Jobs'}
            onSelect={getJobType}
            options={crmJobsTypes}
          />
        </Grid>
        <Grid item>
          {isAuthorizedAddCrmJob && (
            <WsButton
              startIcon={<AddIcon />}
              size="medium"
              onClick={() => setOpenCreateCrmJobDrawer(true)}
            >
              New Job
            </WsButton>
          )}
        </Grid>
      </Grid>
      {jobs && (
        <Grid container item xs={12} columnSpacing={1} ml={0}>
          <StyledGrid item xs={openAdvancedFilters ? 9 : 12}>
            <CrmJobFilters
              onAdvancedFiltersClick={() => setOpenAdvancedFilters(!openAdvancedFilters)}
              searchQuery={searchQuery ?? {}}
              getUpdatedFiltersFromFilters={(query) => setSearchQuery(query)}
              clear={onClear}
              handleDownload={handleDownload}
            />
            <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
              {loading ? (
                <WsListingLoader />
              ) : (
                <>
                  {isEmpty(jobs.content) ? (
                    <NoDataFoundCard
                      text="No Crm Jobs to show for applied filter(s)"
                      icon={<WsIconNoUserGeneric />}
                      subElement={<WsTypography>No Crm Jobs</WsTypography>}
                    />
                  ) : (
                    <CrmJobData
                      crmJobsData={jobs.content}
                      totalRecord={jobs.totalElements}
                      sortModel={sortModel}
                      setSortModel={setSortModel}
                      fetchCrmJobs={fetchCrmJobs}
                    />
                  )}
                </>
              )}
            </ErrorBoundary>
          </StyledGrid>
          {openAdvancedFilters && (
            <Grid item xs={3}>
              <CrmJobsAdvanceFilters
                onClose={() => setOpenAdvancedFilters(false)}
                defaultValues={advancedFilterDefaults}
                onSubmit={onFilterFormSubmit}
              />
            </Grid>
          )}
        </Grid>
      )}
      {openCreateCrmJobDrawer && (
        <CrmJobDrawer
          open={openCreateCrmJobDrawer}
          onClose={() => setOpenCreateCrmJobDrawer(false)}
          crmJobId={null}
          postSubmit={(crmJob: CrmJobDTO) => crmJob && fetchCrmJobs()}
        />
      )}
      <WsDrawer
        title="Download Fields"
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        size="medium"
        headerSize="medium"
      >
        <Box pb={5}>
          <CrmJobDownloadField
            selectedFields={(d) => handleDownloadCrmJobs(d)}
            close={() => setOpenDownload(false)}
            availableColumns={getAllAvailableColumns()}
          />
        </Box>
      </WsDrawer>
    </>
  );
};

export default CrmJobList;
